/**
 * Canada landing page content — the copy from the "StartEngine Canada Homepage"
 * design handoff.
 *
 * Everything here is the fallback layer: the offering-dependent pieces (featured
 * spotlight, the "Available in Canada" shelf) are overridden at runtime by the
 * `canada-landing-pages` Strapi entry via `useCanadaLandingPage`. The static
 * copy below keeps the page complete and legally correct before that entry
 * exists, and is what renders if Strapi is unreachable.
 *
 * Legal copy is reproduced verbatim from the handoff and reviewed by compliance
 * — do not paraphrase it.
 */
import type { HeroStat, Spotlight } from 'components/HomepageV3/data/demoData';
import type { HowToStep } from 'components/HomepageV3/sections/HowToStartSection';
import type { ManifestoColumn } from 'components/HomepageV3/sections/WpManifestoSection';

export const BELCO_EMAIL = 'warren@belcopc.com';
export const BELCO_MAILTO = `mailto:${BELCO_EMAIL}`;
export const PRIVACY_EMAIL = 'privacy@startengine.com';
export const CONTACT_EMAIL = 'contact@startengine.com';

export const CANADA_CTA_LABEL = 'Book a Call';

/* ─────────────── Hero ─────────────── */

export const CANADA_HERO = {
  title: "Access the world's most sought-after ",
  titleEm: 'pre-IPO companies.',
  footnoteMarker: '1',
  ctaLabel: CANADA_CTA_LABEL,
  ctaNote: 'Free to join · No commitment',
  ctaNoteSub: 'Available in Canada to eligible investors only',
} as const;

export const CANADA_HERO_STATS: HeroStat[] = [
  { val: '$1.5B', label: 'Invested on the platform' },
  { val: '2.1M', label: 'Community' },
  { val: '1,000', label: 'Opportunities' },
  { val: '13 Yrs', label: 'In business' },
];

/* ─────────────── Offered-on-StartEngine ticker ─────────────── */

export const CANADA_TICKER_FOOTNOTE_MARKER = '1';

export const CANADA_TICKER_DISCLOSURE =
  'In Canada, StartEngine offerings are made available through Belco Private Capital Inc. under prospectus exemptions. You are investing in units of a fund — not purchasing portfolio company securities directly; portfolio companies are not a party to or endorser of these offerings. Company names and logos below identify companies that have been offered on the StartEngine platform and do not indicate availability to Canadian investors or any current offering.';

/* ─────────────── Featured spotlight ─────────────── */

export const CANADA_SPOTLIGHT_IMAGE = '/homepage-v3/canada/lambda-hero.png';

export const CANADA_SPOTLIGHT: Spotlight = {
  eyebrowLabel: 'Featured Offering',
  series: 'STARTENGINE CA – 01 Limited Partnership',
  name: 'Lambda Labs Canada 1',
  desc: 'Access to Lambda, Inc. (“Lambda Labs”) — a leading AI infrastructure and GPU cloud company — through a membership-interest series, made available to eligible Canadian investors. Managed and distributed in Canada by Belco Private Capital Inc.',
  emphasis: ['Managed and distributed in Canada by Belco Private Capital Inc.'],
  cta: CANADA_CTA_LABEL,
  image: CANADA_SPOTLIGHT_IMAGE,
};

export const CANADA_SPOTLIGHT_LEGAL =
  'This offering is for membership interests in STARTENGINE CA – 01 LIMITED PARTNERSHIP (“Lambda Labs Canada 1”), which will own Series Seed Preferred of Lambda, Inc. (“Lambda Labs”). You are not buying Lambda Labs shares directly. Belco Private Capital Inc. acts as investment fund manager, portfolio manager and exempt market dealer in Canada. This investment is speculative, illiquid, and involves a high degree of risk, including the possible loss of your entire investment. Any offering is made only pursuant to the relevant offering documents together with the subscription agreement, both of which should be read in their entirety.';

/* ─────────────── "Available in Canada" shelf ─────────────── */

export const CANADA_SHELF = {
  eyebrow: 'Now available',
  title: 'Available in Canada',
  desc: 'The offering currently available to eligible Canadian investors through Belco Private Capital Inc.',
} as const;

export const CANADA_SHELF_CARD = {
  companyKey: 'lambda-labs-canada-1',
  name: 'Lambda Labs Canada 1',
  mono: 'λ',
  series: 'STARTENGINE CA – 01 Limited Partnership',
  sector: 'AI Infrastructure · GPU Cloud',
  badgeLabel: 'Now Investing',
  metaPill: 'Eligible investors only',
  desc: 'Access to Lambda Labs, a leading AI infrastructure and GPU cloud company, through a membership-interest series offered in Canada under prospectus exemptions.',
  minimum: '$25,000 USD',
  structure: 'Membership',
  fillPercent: 46,
  disclosure:
    'This offering is for membership interests in StartEngine CA - 01 Limited Partnership, which will own Series Seed Preferred of Lambda, Inc. (“Lambda Labs”). You are not buying Lambda Labs shares directly. See full details in footnote 1.',
} as const;

/* ─────────────── Portfolio-strategy quote band ─────────────── */

export const CANADA_QUOTE_DISCLAIMER =
  'This statement reflects the view of the individual cited as of the date noted and is provided for general context. It is not an endorsement of StartEngine, Belco Private Capital Inc., or any offering, and is not investment advice or a recommendation.';

/* ─────────────── "Why Invest Pre-IPO" manifesto ─────────────── */

export const CANADA_MANIFESTO_HEADLINE = 'Pre-IPO investing used to happen behind closed doors.';

export const CANADA_MANIFESTO_COLUMNS: ManifestoColumn[] = [
  {
    body: "Companies are waiting longer than ever to go public — and that's where a great deal of value gets built.",
  },
  {
    body: 'For most people, investing in that phase simply wasn’t an option. It was reserved for major venture capital firms and institutional investors.',
  },
  {
    body: (
      <>
        <em>StartEngine changes that.</em> Now eligible Canadian investors can invest in the
        startups and growth companies shaping the future.
      </>
    ),
  },
];

export const CANADA_MANIFESTO_DISCLAIMER =
  'Investments in private companies are speculative, illiquid, and involve a high degree of risk, including the possible loss of your entire investment. Most early-stage and pre-IPO companies do not achieve an IPO or other liquidity event. Historical figures and past performance do not predict future results. No return, liquidity event, or IPO is promised or guaranteed.';

/* ─────────────── "How to Start Investing" ─────────────── */

export const CANADA_HOW_TO_LEAD =
  'Tapping into pre-IPO opportunities is easier than you think. In Canada, Belco Private Capital Inc. handles eligibility, suitability, and subscriptions — so you can focus on the companies you believe in.';

export const CANADA_HOW_TO_STEPS: HowToStep[] = [
  {
    title: 'Explore',
    desc: 'Browse pre-IPO and private market offerings available to Canadian investors. Learn about the fund, its structure, and the companies it targets.',
  },
  {
    title: 'Connect & Verify',
    desc: "Create a free account, then connect with Belco Private Capital Inc. — StartEngine's exempt market dealer in Canada. Belco confirms your eligibility and assesses whether an offering is suitable for you.",
  },
  {
    title: 'Invest',
    desc: 'Review the full offering documents, disclosures, and subscription agreement. Minimums typically start at $10,000 CAD. Warren Aarons and the Belco team can walk you through any offering before you commit.',
  },
  {
    title: 'Track Performance',
    desc: 'Track your holdings in your StartEngine dashboard. When a liquidity event occurs — an IPO, acquisition, or private sale — the Belco team walks you through your options.',
  },
];

/* ─────────────── Bottom CTA band ─────────────── */

export const CANADA_APP_BAND = {
  accessText:
    'Access is limited. Offerings close when subscriptions fill or reach a closing date — ',
  accessEm: 'create a free account',
  accessTail: ' and get notified when new companies become available in Canada.',
  signupLabel: CANADA_CTA_LABEL,
} as const;

/* ─────────────── Footer ─────────────── */

export const CANADA_LEGAL_LAST_UPDATED = 'Last updated: July 8, 2026';
